/**
 * Admin tenant hooks.
 *
 * Endpoints:
 *   GET    /admin/tenants                                  — list (filter+sort+paginate, Batch 27h)
 *   GET    /admin/tenants/{id}                             — detail (counts/plan/credits/recent_activity, Batch 27h)
 *   POST   /admin/tenants                                  — provision new
 *   PUT    /admin/tenants/{id}                             — update name (Batch 27h)
 *   POST   /admin/tenants/{id}/suspend                     — Batch 27h
 *   POST   /admin/tenants/{id}/reinstate                   — Batch 27h
 *   GET    /admin/tenants/{id}/users                       — Batch 27h
 *   POST   /admin/tenants/{id}/users/{uid}/verify-email    — Batch 27h
 *   POST   /admin/tenants/{id}/users/{uid}/resend-verification — Batch 27h
 *
 * Batch 27h drive-by fix: ``CreateTenantBody`` now sends
 * ``admin_first_name + admin_last_name`` (matches backend since 27f) — the
 * pre-existing ``admin_name: string`` field was a stale 27e holdover and the
 * backend would reject the create call as 422 ``extra_forbidden``.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { queryClient } from "@/lib/queryClient";

// ── Types ────────────────────────────────────────────────────────────────────

interface TenantListItem {
  id: string;
  slug: string;
  name: string;
  status: string;
  stripe_customer_id: string | null;
  trial_ends_at: string | null;
  created_at: string;
  // Batch 27h enrichment
  user_count: number;
  plan_name: string | null;
  current_credits: number;
}

interface ListTenantsResponse {
  tenants: TenantListItem[];
  total_count: number;
  limit: number;
  offset: number;
}

interface PlanSummary {
  id: string;
  name: string;
  price_cents: number;
  credits_per_month: number;
}

export interface ActivityLogItem {
  id: string;
  action: string;
  user_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** B-24b WS-1 — contract record (null = no contract row yet). */
interface TenantContractSummary {
  contract_value_cents: number | null;
  contract_term_start: string | null;
  contract_term_end: string | null;
  account_owner: string | null;
  /** draft | pilot | active | expired | churned. 'active' = contracted. */
  lifecycle_state?: string;
}

export interface TenantDetail {
  id: string;
  slug: string;
  name: string;
  status: string;
  stripe_customer_id: string | null;
  trial_ends_at: string | null;
  /** Whether the activation/trial drip emails send to this tenant. SA-editable. */
  trial_drip_enabled: boolean;
  pending_deletion_at: string | null;
  created_at: string;
  company_website: string | null;
  user_count: number;
  plan: PlanSummary | null;
  current_credits: number;
  recent_activity: ActivityLogItem[];
  /** B-19 — anti-abuse seat cap. SA-editable. */
  max_users: number;
  /** B-24b WS-1 — contract record (null = no contract row yet). */
  contract?: TenantContractSummary | null;
}

export interface TenantUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_verified: boolean;
  is_super_admin: boolean;
  email_deliverable: boolean;
  email_undeliverable_reason: string | null;
  created_at: string;
}

interface ListTenantUsersResponse {
  users: TenantUser[];
  total_count: number;
  limit: number;
  offset: number;
}

interface CreateTenantBody {
  company_name: string;
  slug?: string | null;
  admin_email: string;
  // Batch 27h drive-by fix: split per Batch 27f backend contract.
  admin_first_name: string;
  admin_last_name: string;
  plan_id?: string | null;
  company_website?: string | null;
  terms_version: string;
  dpa_version: string;
  privacy_version: string;
  // Manual per-contract trial length (trial path only). Provide a length in
  // days (defaults to the 45-day free pilot on the backend when omitted) OR an
  // explicit end timestamp — not both. See backend CreateTenantBody.
  trial_length_days?: number | null;
  trial_ends_at?: string | null;
}

interface ProvisionTenantResponse {
  tenant: TenantListItem;
  admin_user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: "admin";
    is_verified: boolean;
  };
  initial_credits: number;
  subscription: null;
}

export type TenantSortKey =
  | "created_at_desc"
  | "created_at_asc"
  | "name_asc"
  | "name_desc";

export type TenantStatusFilter =
  | "all"
  | "active"
  | "suspended"
  | "pending_deletion";

interface ListTenantsQuery {
  status?: TenantStatusFilter;
  sort?: TenantSortKey;
  q?: string;
  limit?: number;
  offset?: number;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useAdminTenants(params: ListTenantsQuery = {}) {
  const status = params.status ?? "all";
  const sort = params.sort ?? "created_at_desc";
  const q = params.q ?? "";
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;

  return useQuery<ListTenantsResponse, ApiError>({
    queryKey: ["admin", "tenants", { status, sort, q, limit, offset }],
    queryFn: async () => {
      const url = new URL("/admin/tenants", "http://placeholder");
      if (status !== "all") url.searchParams.set("status", status);
      url.searchParams.set("sort", sort);
      if (q.trim().length > 0) url.searchParams.set("q", q.trim());
      url.searchParams.set("limit", String(limit));
      url.searchParams.set("offset", String(offset));
      const path = `${url.pathname}?${url.searchParams.toString()}`;
      const r = await apiClient.get<ListTenantsResponse>(path);
      return r.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminTenant(tenantId: string | undefined) {
  return useQuery<TenantDetail, ApiError>({
    queryKey: ["admin", "tenants", tenantId],
    queryFn: async () => {
      const r = await apiClient.get<TenantDetail>(`/admin/tenants/${tenantId}`);
      return r.data;
    },
    enabled: !!tenantId,
  });
}

export function useProvisionTenant() {
  return useMutation<ProvisionTenantResponse, ApiError, CreateTenantBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<ProvisionTenantResponse>(
        "/admin/tenants",
        body,
      );
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

// Batch 27h — Lane A PUT, extended in B-19.
interface UpdateTenantBody {
  name?: string;
  /** B-19 — anti-abuse seat cap override. Must be >= 1 and >= current active users. */
  max_users?: number;
  /** SA trial control — set/extend the pilot end (ISO). Absent = unchanged. */
  trial_ends_at?: string;
  /** SA trial control — toggle the activation/trial drip for this tenant. */
  trial_drip_enabled?: boolean;
}

export function useUpdateTenant(tenantId: string) {
  return useMutation<TenantDetail, ApiError, UpdateTenantBody>({
    mutationFn: async (body) => {
      const r = await apiClient.put<TenantDetail>(
        `/admin/tenants/${tenantId}`,
        body,
      );
      return r.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.setQueryData(["admin", "tenants", tenantId], data);
    },
  });
}

/**
 * Convert a trial tenant to a contracted customer: clears trial_ends_at AND
 * moves the contract lifecycle to 'active'. Does not touch credits/money.
 */
export function useConvertToContracted(tenantId: string) {
  return useMutation<TenantDetail, ApiError, void>({
    mutationFn: async () => {
      const r = await apiClient.post<TenantDetail>(
        `/admin/tenants/${tenantId}/convert-to-contracted`,
        {},
      );
      return r.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.setQueryData(["admin", "tenants", tenantId], data);
    },
  });
}

// Batch 27h — Lane B users + verify + resend.
export function useTenantUsers(tenantId: string | undefined) {
  return useQuery<ListTenantUsersResponse, ApiError>({
    queryKey: ["admin", "tenants", tenantId, "users"],
    queryFn: async () => {
      const r = await apiClient.get<ListTenantUsersResponse>(
        `/admin/tenants/${tenantId}/users`,
      );
      return r.data;
    },
    enabled: !!tenantId,
  });
}

export function useVerifyUser(tenantId: string) {
  return useMutation<TenantUser, ApiError, string>({
    mutationFn: async (userId) => {
      const r = await apiClient.post<TenantUser>(
        `/admin/tenants/${tenantId}/users/${userId}/verify-email`,
      );
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId, "users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId],
      });
    },
  });
}

export function useResendVerification(tenantId: string) {
  return useMutation<
    { sent: boolean; reason: string | null },
    ApiError,
    string
  >({
    mutationFn: async (userId) => {
      const r = await apiClient.post<{
        sent: boolean;
        reason: string | null;
      }>(`/admin/tenants/${tenantId}/users/${userId}/resend-verification`);
      return r.data;
    },
  });
}

// SA user management — edit, force password reset, clear lockout.

interface EditTenantUserBody {
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface EditTenantUserResponse {
  user_id: string;
  fields_updated: string[];
}

export function useEditTenantUser(tenantId: string) {
  return useMutation<
    EditTenantUserResponse,
    ApiError,
    { userId: string; body: EditTenantUserBody }
  >({
    mutationFn: async ({ userId, body }) => {
      const r = await apiClient.patch<EditTenantUserResponse>(
        `/admin/tenants/${tenantId}/users/${userId}`,
        body,
      );
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId, "users"],
      });
    },
  });
}

interface ForcePasswordResetResponse {
  message: string;
  token_expires_at: string;
}

export function useForcePasswordReset(tenantId: string) {
  return useMutation<ForcePasswordResetResponse, ApiError, string>({
    mutationFn: async (userId) => {
      const r = await apiClient.post<ForcePasswordResetResponse>(
        `/admin/tenants/${tenantId}/users/${userId}/force-password-reset`,
      );
      return r.data;
    },
  });
}

export function useClearLockout(tenantId: string) {
  return useMutation<{ message: string }, ApiError, string>({
    mutationFn: async (userId) => {
      const r = await apiClient.post<{ message: string }>(
        `/admin/tenants/${tenantId}/users/${userId}/clear-lockout`,
      );
      return r.data;
    },
  });
}

// D18 — SA MFA-policy toggle (recover a stuck tenant/demo without a reset).
interface SetTenantMfaRequiredResponse {
  tenant_id: string;
  mfa_required: boolean;
}

/**
 * Flip ``tenants.mfa_required`` from the SA console for ANY tenant (regular or
 * demo). Immediate in both directions, ungated by the D17 test-send — the
 * operator escape hatch for MFA lockouts. The recovery-critical use is
 * ``enabled=false`` on a stuck demo tenant.
 */
export function useSetTenantMfaRequired(tenantId: string) {
  return useMutation<SetTenantMfaRequiredResponse, ApiError, boolean>({
    mutationFn: async (enabled) => {
      const r = await apiClient.post<SetTenantMfaRequiredResponse>(
        `/admin/tenants/${tenantId}/mfa-required`,
        { enabled },
      );
      return r.data;
    },
    onSuccess: () => {
      // Refresh the SA tenant detail (regular path) and the demo status
      // (demo path keys off the demo-status query) so the toggle reflects.
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo"] });
    },
  });
}

// P1 — SA dashboard-persona config (per-tenant Intelligence variant).
type DashboardPersona = "agency" | "in_house";
interface SetTenantDashboardPersonaResponse {
  tenant_id: string;
  dashboard_persona: DashboardPersona;
}

/**
 * Set ``tenants.dashboard_persona`` from the SA console for ANY tenant (regular
 * or demo). The Hiring-Intelligence dashboard then renders ONLY this variant
 * (read back via ``/auth/me.dashboard_persona``); there is no user-facing
 * toggle. Mirrors ``useSetTenantMfaRequired`` (D18 pattern).
 */
export function useSetTenantDashboardPersona(tenantId: string) {
  return useMutation<
    SetTenantDashboardPersonaResponse,
    ApiError,
    DashboardPersona
  >({
    mutationFn: async (persona) => {
      const r = await apiClient.post<SetTenantDashboardPersonaResponse>(
        `/admin/tenants/${tenantId}/dashboard-persona`,
        { persona },
      );
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo"] });
    },
  });
}

// Batch 27h — Lane C suspend / reinstate.
interface TenantStatusActionBody {
  reason: string;
}

export function useSuspendTenant(tenantId: string) {
  return useMutation<TenantDetail, ApiError, TenantStatusActionBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<TenantDetail>(
        `/admin/tenants/${tenantId}/suspend`,
        body,
      );
      return r.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.setQueryData(["admin", "tenants", tenantId], data);
    },
  });
}

export function useReinstateTenant(tenantId: string) {
  return useMutation<TenantDetail, ApiError, TenantStatusActionBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<TenantDetail>(
        `/admin/tenants/${tenantId}/reinstate`,
        body,
      );
      return r.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.setQueryData(["admin", "tenants", tenantId], data);
    },
  });
}

// Batch P2 — manual super-admin credit top-up.
interface TopUpCreditsBody {
  amount: number;
  reason: string;
  idempotency_key?: string;
}

interface TopUpCreditsResponse {
  ledger_id: string;
  tenant_id: string;
  amount: number;
  balance_after: number;
  action: "admin_topup";
  idempotency_replayed: boolean;
  created_at: string;
}

// Batch SA-WIPE — irreversible super-admin tenant force-wipe.
interface ForceWipeBody {
  confirm_slug: string;
  reason: string;
}

interface ForceWipeResponse {
  tenant_id: string;
  row_counts: Record<string, number>;
  r2_purge_status: "complete" | "partial";
  r2_remaining_keys: string[];
  audit_event_id: string;
}

export function useForceWipeMutation(tenantId: string) {
  return useMutation<ForceWipeResponse, ApiError, ForceWipeBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<ForceWipeResponse>(
        `/admin/tenants/${tenantId}/force-wipe`,
        body,
      );
      return r.data;
    },
    onSuccess: () => {
      // Tenant is gone — drop the detail cache and refetch the list.
      queryClient.removeQueries({ queryKey: ["admin", "tenants", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

export function useTopUpCredits(tenantId: string) {
  return useMutation<TopUpCreditsResponse, ApiError, TopUpCreditsBody>({
    mutationFn: async (body) => {
      const r = await apiClient.post<TopUpCreditsResponse>(
        `/admin/tenants/${tenantId}/credits/topup`,
        body,
      );
      return r.data;
    },
    onSuccess: () => {
      // Invalidate the tenant detail (current_credits + recent_activity)
      // and the tenants list (current_credits column on the table).
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenants", tenantId],
      });
    },
  });
}

// B-24b WS-1 — contract upsert.
interface UpsertContractBody {
  contract_value_cents: number | null;
  contract_term_start: string | null;
  contract_term_end: string | null;
  account_owner: string | null;
  // DC2 screening cap (fair_use_ceiling). Optional on the wire — the backend
  // UpsertContractBody defaults it to null (unlimited / fail-open) when absent,
  // and validates >= 1 when present. Settable at provisioning (TenantCreate).
  fair_use_ceiling?: number | null;
}

export function useUpsertTenantContract(tenantId: string) {
  return useMutation<TenantDetail, ApiError, UpsertContractBody>({
    mutationFn: async (body) => {
      const r = await apiClient.put<TenantDetail>(
        `/admin/tenants/${tenantId}/contract`,
        body,
      );
      return r.data;
    },
    onSuccess: (data) => {
      // Update the detail cache with the fresh response (includes new contract).
      queryClient.setQueryData(["admin", "tenants", tenantId], data);
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}
